import { QuantitySelectorComponent } from "@theme/component-quantity-selector";
import { QuantitySelectorUpdateEvent } from "@theme/events";

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  #loading = false;

  /** @type {{ input: boolean, minus: boolean, plus: boolean } | null} */
  #prevDisabled = null;

  /**
   * Sets loading state for this quantity selector.
   * In GR cart drawer we avoid optimistic UI updates: we show a loader over the number and disable controls.
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    if (!this.initialized) return;
    if (this.#loading === isLoading) return;

    const { quantityInput, minusButton, plusButton } = this.refs;

    if (isLoading) {
      this.#prevDisabled = {
        input: quantityInput.disabled,
        minus: minusButton.disabled,
        plus: plusButton.disabled,
      };
    }

    this.#loading = isLoading;

    const loader = this.querySelector("[data-qty-loader]");
    if (loader instanceof HTMLElement) {
      loader.classList.toggle("hidden", !isLoading);
    }

    this.classList.toggle("gr-cart-qty--loading", isLoading);
    this.setAttribute("aria-busy", isLoading ? "true" : "false");

    if (isLoading) {
      minusButton.disabled = true;
      plusButton.disabled = true;
      quantityInput.disabled = true;
      return;
    }

    if (this.#prevDisabled) {
      quantityInput.disabled = this.#prevDisabled.input;
      minusButton.disabled = this.#prevDisabled.minus;
      plusButton.disabled = this.#prevDisabled.plus;
      this.#prevDisabled = null;
    }

    // Recompute boundaries after unlock
    this.updateButtonStates();
  }

  /** @returns {boolean} */
  #isInGRCartDrawer() {
    return Boolean(this.closest("gr-cart-drawer"));
  }

  /**
   * Dispatches a cart quantity update without optimistically changing the visible number.
   * @param {number} nextQuantity
   */
  #dispatchCartQuantity(nextQuantity) {
    const { quantityInput } = this.refs;
    const cartLine = Number(quantityInput.dataset.cartLine);
    if (!cartLine) return;
    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(nextQuantity, cartLine),
    );
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /** @param {Event} event */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    // If we're in the GR cart drawer, lock UI immediately and avoid optimistic number changes.
    if (this.#isInGRCartDrawer()) {
      if (this.#loading) return;

      const { min, step, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();
      const nextValue = Math.min(
        effectiveMax ?? Infinity,
        Math.max(min, value + step),
      );

      // No-op if it wouldn't change
      if (nextValue === value) return;

      this.setLoading(true);
      this.#dispatchCartQuantity(nextValue);
      return;
    }

    super.increaseQuantity(event);
  }

  /** @param {Event} event */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    if (this.#isInGRCartDrawer()) {
      if (this.#loading) return;

      const { min, step, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();
      const nextValue = Math.min(
        effectiveMax ?? Infinity,
        Math.max(min, value - step),
      );
      if (nextValue === value) return;

      this.setLoading(true);
      this.#dispatchCartQuantity(nextValue);
      return;
    }

    super.decreaseQuantity(event);
  }

  /** @param {Event} event */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();

    if (this.#isInGRCartDrawer()) {
      if (this.#loading) return;

      const { quantityInput } = this.refs;
      const { min, step } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();

      const raw = parseInt(event.target.value) || 0;
      const clamped = Math.min(effectiveMax ?? Infinity, Math.max(min, raw));

      // Validate step increment like base class
      if ((clamped - min) % step !== 0) {
        quantityInput.value = clamped.toString();
        quantityInput.reportValidity();
        return;
      }

      const current = parseInt(quantityInput.value) || 0;
      if (clamped === current) return;

      // Reset the visible value back (we'll wait for server) and lock.
      quantityInput.value = current.toString();
      this.setLoading(true);
      this.#dispatchCartQuantity(clamped);
      return;
    }

    super.setQuantity(event);
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (this.#loading) {
      minusButton.disabled = true;
      plusButton.disabled = true;
      return;
    }

    // Check if increase is permanently disabled (e.g., nugget bundles)
    const disableIncrease = this.dataset.disableIncrease === "true";

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled =
      disableIncrease || (effectiveMax !== null && value >= effectiveMax);
  }
}

if (!customElements.get("cart-quantity-selector-component")) {
  customElements.define(
    "cart-quantity-selector-component",
    CartQuantitySelectorComponent,
  );
}
